import { useCallback, useContext, useEffect, useMemo } from 'react'

import { Grid, Stack, Tooltip } from '@mui/material'
import Decimal from 'decimal.js'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import { CardTitle, TabPanelBootstrap, FplDynamicTabs } from 'components/common'
import {
  DEBOUNCE_DELAY,
  DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID,
  DeliveryIntervals,
  IgnoredXeroServiceCodes,
  Roles,
} from 'constants/index'
import {
  BookingAddressType,
  GoogleDistanceStatus,
  useDeleteBookingAddressMutation,
  useGetDistanceCalculationLazyQuery,
  GetDistanceCalculationQuery,
} from 'generated/graphql'
import { isPageLoadingVar } from 'graphql/reactiveVariables'
import {
  calculateBusinessTransitInterval,
  calculateTransitInterval,
  getGoogleAddressInput,
} from 'helpers'
import usePrevious from 'helpers/usePrevious'
import { useUserRole } from 'hooks'
import { usePermission, emitter } from 'providers'
import { FillIntervalParams, TBookingForm } from 'types'

import { AddressContext } from './AddressProvider'
import { BookingAddress, getBookingAddressDefaultValues } from './BookingAddress'
import { DistanceInput } from './DistanceInput'

interface IProps {
  isQuote: boolean
  isQuickQuote: boolean
  isUpdate?: boolean
  hoverText: string
  disabled?: boolean
  currentWaitAndReturnFeeCategoryId?: number
  currentIsWaitAndReturn?: boolean
}
const DeliveryDetails = (props: IProps) => {
  const {
    isQuote,
    isQuickQuote,
    isUpdate,
    hoverText,
    disabled,
    currentIsWaitAndReturn,
    currentWaitAndReturnFeeCategoryId,
  } = props
  const userRole = useUserRole()

  const { deliveryTabValue, setDeliveryTabValue } = useContext(AddressContext)

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<TBookingForm>()
  const {
    fields: deliveryFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'deliveryAddresses',
    keyName: 'tabsKey',
  })

  const [collectionAt] = useDebounce(useWatch({ name: 'collectionAddresses.0.at' }), DEBOUNCE_DELAY)
  const [collectionBy] = useDebounce(useWatch({ name: 'collectionAddresses.0.by' }), DEBOUNCE_DELAY)
  const serviceType = useWatch({ name: 'serviceType' })
  const collectionAddresses = useWatch({ name: 'collectionAddresses' })
  const isWaitAndReturn = useWatch({ name: 'isWaitAndReturn' })
  const prevIsWaitAndReturn = usePrevious<boolean>(isWaitAndReturn)
  const fieldsLength = deliveryFields.length
  const prevFieldsLength = usePrevious<number>(deliveryFields.length)

  const [isAllowedToUpdateBookingAfterQuoteIsBooked] = usePermission(
    'UpdateBookingAfterQuoteIsBooked',
  )

  const showAddAndRemoveButtons = isQuote || isAllowedToUpdateBookingAfterQuoteIsBooked
  const showAddButton = showAddAndRemoveButtons && !isWaitAndReturn
  const showRemoveButton = showAddAndRemoveButtons

  const isWaitAndReturnModified = useMemo(() => {
    if (userRole !== Roles.customer) return false

    return (
      currentIsWaitAndReturn &&
      currentWaitAndReturnFeeCategoryId !== undefined &&
      currentWaitAndReturnFeeCategoryId !== Number(DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID)
    )
  }, [userRole, currentIsWaitAndReturn, currentWaitAndReturnFeeCategoryId])

  // Queries
  const handleDistanceCalculation = useCallback(
    (data: GetDistanceCalculationQuery, distanceType: 'distance' | 'returnDistance') => {
      const { status, distance: rawDistance, errorMessage } = data.distanceCalculation

      if (status === GoogleDistanceStatus.Success) {
        const distance = new Decimal(rawDistance).toDP(2, Decimal.ROUND_FLOOR).toNumber()

        setValue(distanceType, distance, { shouldDirty: true, shouldValidate: true })
      } else if (status === GoogleDistanceStatus.Error) {
        toast.warning(errorMessage)
      }
    },
    [setValue],
  )

  const [getDistanceCalculation, { loading: distanceCalculationLoading }] =
    useGetDistanceCalculationLazyQuery({
      onError: (error) => {
        toast.error(error.message)
      },
    })

  // Mutations
  const [deleteBookingAddress] = useDeleteBookingAddressMutation()

  const deliveryAddressesErrors = errors?.deliveryAddresses
  const errorTabs = useMemo(
    () =>
      (deliveryAddressesErrors as any)?.map((value, index) => (value !== undefined ? index : null)),
    [deliveryAddressesErrors],
  )

  // Handle isWaitAndReturn logic
  useEffect(() => {
    const collectionAddress = collectionAddresses?.[0]
    if (!collectionAddress || isWaitAndReturn === prevIsWaitAndReturn) return

    const isSameAddress = (a: typeof collectionAddress, b: typeof collectionAddress) =>
      a.addressLine1 === b.addressLine1 && a.postcode === b.postcode

    const computeNextSequence = () => {
      const all = [
        ...(getValues('deliveryAddresses') ?? []),
        ...(getValues('collectionAddresses') ?? []),
      ]
      return Math.max(0, ...all.map((x: any) => x.sequenceOrder || 0)) + 1 || 1
    }

    const buildReturnAddress = (seq: number) => ({
      ...getBookingAddressDefaultValues(seq),
      name: collectionAddress.name,
      city: collectionAddress.city,
      addressLine1: collectionAddress.addressLine1,
      addressLine2: collectionAddress.addressLine2 || '',
      postcode: collectionAddress.postcode,
      countryId: collectionAddress.countryId,
      contact: collectionAddress.contact || '',
      email: collectionAddress.email || '',
      telephoneNumber: collectionAddress.telephoneNumber || '',
      note: collectionAddress.note || '',
    })

    const recalculateDistance = (deliveryAddrs: any[]) => {
      const googleInput = getGoogleAddressInput(getValues('collectionAddresses'), deliveryAddrs)
      const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

      if (googleInput.length >= 2) {
        getDistanceCalculation({ variables: { input: googleInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        if (isWaitAndReturn) {
          const lastTwoAddresses = googleInput.slice(-2)
          getDistanceCalculation({ variables: { input: lastTwoAddresses } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }

    const addReturnAddress = () => {
      const deliveryAddrs = getValues('deliveryAddresses') ?? []
      const hasReturn = deliveryAddrs.some((addr) => isSameAddress(addr, collectionAddress))
      if (hasReturn) return

      const returnAddress = buildReturnAddress(computeNextSequence())
      append(returnAddress)

      const updated = [...deliveryAddrs, returnAddress]
      recalculateDistance(updated)
    }

    const removeReturnAddress = async () => {
      const deliveryAddrs = getValues('deliveryAddresses') ?? []
      const index = deliveryAddrs.findIndex((a) => isSameAddress(a, collectionAddress))
      if (index === -1) return

      const target = deliveryAddrs[index]

      if (target.id) {
        const result = await deleteBookingAddress({
          variables: { bookingAddressId: Number(target.id) },
        })
        if (!result.data?.deleteBookingAddress) return
      }

      // Adjust active tab
      if (deliveryTabValue === index && deliveryFields.length > 1) {
        setDeliveryTabValue(index > 0 ? index - 1 : 0)
      }

      const filtered = deliveryAddrs.filter((_, i) => i !== index)
      setValue('deliveryAddresses', filtered, { shouldValidate: true, shouldDirty: true })
      recalculateDistance(filtered)
    }

    if (isWaitAndReturn && !prevIsWaitAndReturn) addReturnAddress()
    if (!isWaitAndReturn && prevIsWaitAndReturn) void removeReturnAddress()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaitAndReturn, prevIsWaitAndReturn])

  useEffect(() => {
    if (fieldsLength < Number(prevFieldsLength)) {
      const collectionAddressesValue = getValues('collectionAddresses')
      const deliveryAddressesValue = getValues('deliveryAddresses')

      if (collectionAddressesValue && deliveryAddressesValue) {
        const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

        const googleAddressInput = getGoogleAddressInput(
          collectionAddressesValue,
          deliveryAddressesValue,
        )

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        if (isWaitAndReturn) {
          const lastTwoAddresses = googleAddressInput.slice(-2)
          getDistanceCalculation({ variables: { input: lastTwoAddresses } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }, [
    fieldsLength,
    getDistanceCalculation,
    getValues,
    handleDistanceCalculation,
    prevFieldsLength,
    setValue,
  ])

  useEffect(() => {
    // is valid xero service type for auto pop values
    if (!serviceType || IgnoredXeroServiceCodes.includes(serviceType)) {
      return
    }

    const collectionAddresses = getValues('collectionAddresses')
    const deliveryAddresses = getValues('deliveryAddresses')

    // is not multi-drop
    if (collectionAddresses.length + deliveryAddresses.length !== 2) {
      return
    }

    const deliveryInterval = DeliveryIntervals[serviceType]

    if (!deliveryInterval) {
      return
    }

    const intervalParams: FillIntervalParams = {
      transitInterval: deliveryInterval,
      collectionAt: collectionAt ? new Date(collectionAt) : undefined,
      collectionBy: collectionBy ? new Date(collectionBy) : undefined,
      deliveryAddress: deliveryAddresses[0],
      onDeliveryAtSet: (deliveryAt: Date) => setValue('deliveryAddresses.0.at', deliveryAt),
      onDeliveryBySet: (deliveryBy: Date) => setValue('deliveryAddresses.0.by', deliveryBy),
    }

    if (deliveryInterval.workingPeriod) {
      calculateBusinessTransitInterval(intervalParams)
    } else {
      calculateTransitInterval(intervalParams)
    }
  }, [serviceType, getValues, setValue, collectionAt, collectionBy])

  useEffect(() => {
    isPageLoadingVar(distanceCalculationLoading)
  }, [distanceCalculationLoading])

  const handleTabChange = (_, newValue) => {
    setDeliveryTabValue(newValue)
  }

  const handleAddClick = () => {
    const newTabValue = deliveryFields.length
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')
    const newSequenceOrder =
      [...collectionAddressesValue, ...deliveryAddressesValue].reduce(
        (max, address) => (address.sequenceOrder > max ? address.sequenceOrder : max),
        0,
      ) + 1

    const newAddress = getBookingAddressDefaultValues(newSequenceOrder)

    if (deliveryAddressesValue[0].reasonCodeId)
      newAddress.reasonCodeId = deliveryAddressesValue[0].reasonCodeId

    append(newAddress)
    setDeliveryTabValue(newTabValue)

    emitter.emit('add-drop-charge')
  }

  const handleRemoveClick = (e, index: number, id?: string) => {
    e.stopPropagation()

    // Check if this is a return address (matches collection address) before removing
    const deliveryAddressesValue = getValues('deliveryAddresses')
    const collectionAddressesValue = getValues('collectionAddresses')
    const addressToRemove = deliveryAddressesValue[index]

    const isReturnAddress =
      collectionAddressesValue?.[0] &&
      addressToRemove.addressLine1 === collectionAddressesValue[0].addressLine1 &&
      addressToRemove.postcode === collectionAddressesValue[0].postcode

    if (isReturnAddress && isWaitAndReturnModified) {
      toast.error('The delivery address related to Wait & Return cannot be removed.')
      return
    }

    if (id) {
      deleteBookingAddress({ variables: { bookingAddressId: Number(id) } }).then((result) => {
        if (!result.data?.deleteBookingAddress) {
          return
        }

        if (deliveryTabValue === deliveryFields.length - 1) {
          setDeliveryTabValue(deliveryFields.length - 2)
        }
        remove(index)

        // If removing a return address and isWaitAndReturn is true, uncheck it
        if (isReturnAddress && isWaitAndReturn) {
          setValue('isWaitAndReturn', false)
        }

        toast.success('Address was deleted with success')

        emitter.emit('add-drop-charge')
      })
    } else {
      if (deliveryTabValue === deliveryFields.length - 1) {
        setDeliveryTabValue(deliveryFields.length - 2)
      }
      remove(index)

      // If removing a return address and isWaitAndReturn is true, uncheck it
      if (isReturnAddress && isWaitAndReturn) {
        setValue('isWaitAndReturn', false)
      }

      emitter.emit('add-drop-charge')
    }
  }

  const handleFindOrAf = () => {
    const collectionAddressesValue = getValues('collectionAddresses')
    const deliveryAddressesValue = getValues('deliveryAddresses')

    if (collectionAddressesValue && deliveryAddressesValue) {
      const googleAddressInput = getGoogleAddressInput(
        collectionAddressesValue,
        deliveryAddressesValue,
      )

      if (googleAddressInput.length >= 2) {
        const isWaitAndReturn = getValues('isWaitAndReturn') ?? false

        getDistanceCalculation({ variables: { input: googleAddressInput } }).then(
          ({ data }) => data && handleDistanceCalculation(data, 'distance'),
        )

        if (isWaitAndReturn) {
          const lastTwoAddresses = googleAddressInput.slice(-2)
          getDistanceCalculation({ variables: { input: lastTwoAddresses } }).then(
            ({ data }) => data && handleDistanceCalculation(data, 'returnDistance'),
          )
        } else {
          setValue('returnDistance', 0, { shouldDirty: true })
        }
      }
    }
  }

  const isReturnAddress = (addressIndex: number) => {
    if (!isWaitAndReturn || !collectionAddresses?.[0]) {
      return false
    }

    const deliveryAddressesValue = getValues('deliveryAddresses')
    const deliveryAddress = deliveryAddressesValue[addressIndex]
    const collectionAddress = collectionAddresses[0]

    return (
      deliveryAddress?.addressLine1 === collectionAddress.addressLine1 &&
      deliveryAddress?.postcode === collectionAddress.postcode
    )
  }

  return (
    <Stack spacing={1}>
      <Grid container justifyContent='space-between' rowSpacing={1}>
        <Grid item>
          <CardTitle marginBottom={0} disabled={disabled}>
            Delivery
          </CardTitle>
        </Grid>

        <Grid item ml='auto'>
          <DistanceInput isQuickQuote={isQuickQuote} disabled={disabled} />
        </Grid>
      </Grid>

      <Stack>
        <FplDynamicTabs
          tabValue={deliveryTabValue}
          fields={deliveryFields}
          name='delivery-address'
          label='Add'
          disabled={disabled}
          showAddButton={showAddButton}
          showRemoveButton={showRemoveButton}
          errorTabs={errorTabs}
          onTabChange={handleTabChange}
          onAddClick={handleAddClick}
          onRemoveClick={handleRemoveClick}
        />

        <Tooltip title={disabled ? hoverText : ''} followCursor>
          <div>
            {deliveryFields.map((item, index) => (
              <TabPanelBootstrap value={deliveryTabValue} index={index} key={item.tabsKey}>
                <BookingAddress
                  bookingAddressType={BookingAddressType.Delivery}
                  disabled={disabled}
                  disableAllExceptAtBy={isReturnAddress(index)}
                  isQuote={isQuote}
                  isQuickQuote={isQuickQuote}
                  isUpdate={isUpdate}
                  item={item}
                  index={index}
                  key={item.tabsKey}
                  onFindOrAf={handleFindOrAf}
                />
              </TabPanelBootstrap>
            ))}
          </div>
        </Tooltip>
      </Stack>
    </Stack>
  )
}

export default DeliveryDetails
